import { Box, Flex, Radio, RadioGroup, Text } from '@chakra-ui/react';
import { Button } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders } from '@fortawesome/pro-regular-svg-icons';
import { fetchWorkspaceJournals } from 'src/api/queries';
import { ISubmissionContextJournal } from '@publishing-engine/domain/views';
import { NoRecordFound } from 'src/pages/ReviewList/EmptyState';
import ManageReviewerSearch from './ManageReviewerSearch';

interface Props {
  workspaceId: string;
}

const ReviewerSearchUsersPanel = ({ workspaceId }: Props) => {
  const [journalSelectBoxOpen, setJournalSelectBoxOpen] = useState(false);
  const [selectedJournal, setSelectedJournal] =
    useState<ISubmissionContextJournal>();

  const { data } = useQuery({
    queryKey: ['workspace-journals', { workspaceId }],
    queryFn: () => fetchWorkspaceJournals(workspaceId, 0, 999),
    enabled: !!workspaceId
  });
  const journals = (data?.collection ?? []).filter(
    journal =>
      journal.config.reviewerSearch === true &&
      !!journal.config.lexiconProjectId &&
      !!journal.config.lexiconClassId
  );

  // Auto-select when the workspace only has a single journal.
  useEffect(() => {
    if (!selectedJournal && journals.length === 1) {
      setSelectedJournal(journals[0]);
    }
  }, [journals, selectedJournal]);

  // Keep the selected journal in sync with refreshed data.
  useEffect(() => {
    if (!selectedJournal || journals.length === 0) return;
    const updated = journals.find(j => j.id === selectedJournal.id);
    if (updated) {
      setSelectedJournal(updated);
    }
  }, [journals, selectedJournal?.id]);

  const showJournalSelector = journals.length > 1;

  return (
    <Box>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        flexDirection={{ base: 'column', md: 'row' }}
      >
        <Text textStyle="headingSection">
          Reviewer Search Users
          {journals.length === 1 ? ` (${journals[0]?.data.name})` : ''}
        </Text>

        {showJournalSelector && (
          <Box position="relative" display="inline-block">
            <Button
              variant="secondary"
              onClick={() => setJournalSelectBoxOpen(prev => !prev)}
            >
              <Text
                mr={2}
                maxWidth={{ base: '180px', md: '260px', lg: 'none' }}
                overflow={{ base: 'hidden', lg: 'visible' }}
                textOverflow={{ base: 'ellipsis', lg: 'clip' }}
                whiteSpace={{ base: 'nowrap', lg: 'normal' }}
              >
                Journal: {selectedJournal?.data.name ?? 'Select journal'}
              </Text>
              <FontAwesomeIcon icon={faSliders} />
            </Button>

            {journalSelectBoxOpen && (
              <Box
                position="absolute"
                top="100%"
                right={0}
                mt={2}
                zIndex={10}
                bg="white"
                borderRadius="md"
                boxShadow="lg"
                p={3}
                minW="260px"
                maxWidth={{ base: 'calc(100vw - 32px)', md: '320px' }}
                maxH="450px"
                overflowY="auto"
              >
                <RadioGroup
                  value={selectedJournal?.data.shortname}
                  onChange={(shortname: string) => {
                    const journal = journals.find(
                      j => j.data.shortname === shortname
                    );
                    if (journal) {
                      setSelectedJournal(journal);
                      setJournalSelectBoxOpen(false);
                    }
                  }}
                >
                  <Flex direction="column" gap={2}>
                    {journals.map(journal => (
                      <Radio
                        key={journal.data.shortname}
                        value={journal.data.shortname}
                      >
                        {journal.data.name}
                      </Radio>
                    ))}
                  </Flex>
                </RadioGroup>
              </Box>
            )}
          </Box>
        )}
      </Flex>

      {selectedJournal ? (
        <Box mt={4}>
          <ManageReviewerSearch
            key={selectedJournal.id}
            journal={selectedJournal}
          />
        </Box>
      ) : (
        <Box mt={5}>
          <NoRecordFound
            title={journals.length === 0 ? 'No journal available' : 'No journal selected'}
            subTitle="Please select a journal to proceed"
          />
        </Box>
      )}
    </Box>
  );
};

export default ReviewerSearchUsersPanel;
